import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedIcon from '@mui/icons-material/Verified';
import Loader from "app/common/Loader";
import { useEffect, useState } from "react";
import { getCertificationInfo } from "app/services/api/public/validations";
import PageHeader from "../../components/PageHeader/PageHeader";
import CtaButton from "../../components/CtaButton/CtaButton";

const VerificationsCertificates = () => {
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState({ cols: [], content: [] });
    const [filteredContent, setFilteredContent] = useState([]);
    const [searched, setSearched] = useState(false);

    const loadContent = async () => {
        const { be, data } = await getCertificationInfo();
        if (be !== 'ok') return;
        setData({
            cols: [
                { id: 'userNameCompanyInfoName', label: 'Titular', minWidth: 160 },
                { id: 'quantity', label: 'Cantidad (ton)', align: 'center', minWidth: 140 },
                { id: 'material', label: 'Material', align: 'center', minWidth: 140 },
                { id: 'transformerName', label: 'Transformador', align: 'center', minWidth: 160 },
                { id: 'transformationYear', label: 'Año de transformación', align: 'center', minWidth: 160 },
                { id: 'certificate', label: 'Certificado', minWidth: 140 },
            ],
            content: data.certificationInfo,
        });
        setLoading(false);
    };

    const filterRows = () => {
        const filterText = filter.trim().toUpperCase();
        if (filterText === '') return data.content;
        return data.content.filter(row => {
            const value = row['certificate'];
            if (value !== undefined && value !== null) {
                return String(value).toUpperCase().includes(filterText);
            }
            return false;
        });
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(Number.parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (event) => {
        setFilter(event.target.value.toUpperCase());
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleSearch = () => {
        setFilteredContent(filterRows());
        setSearched(true);
        // Cada búsqueda arranca en la primera página de resultados.
        setPage(0);
    };

    useEffect(() => {
        setLoading(true);
        loadContent();
    }, []);

    return (
        <>
            <Loader visible={loading} />

            <PageHeader
                eyebrow="Herramientas"
                title="Validación de certificados"
                subtitle="Consulta la autenticidad y trazabilidad de un certificado ingresando su número."
            />

            <div className="bg-[#F8FAFC] min-h-[calc(100vh-280px)]">
                <div className="max-w-[1400px] mx-auto py-64 px-40">

                    {/* Search card */}
                    <div className="bg-white rounded-20 p-40 mb-32 shadow-[0_2px_16px_rgba(26,86,219,0.07)] border border-[#E2E8F0]">
                        <div className="flex items-center gap-8 mb-24">
                            <VerifiedIcon className="!text-24 text-[#1A56DB]" />
                            <h2 className="text-20 font-bold text-[#1E3A8A]">
                                Buscar certificado
                            </h2>
                        </div>
                        <div className="flex gap-16 items-stretch">
                            <TextField
                                variant="outlined"
                                fullWidth
                                placeholder="Ingresa el número del certificado"
                                value={filter}
                                onChange={handleFilterChange}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                        fontSize: '1.6rem',
                                        height: '100%',
                                        '&:hover fieldset': { borderColor: '#1A56DB' },
                                        '&.Mui-focused fieldset': { borderColor: '#1A56DB' },
                                    },
                                    '& .MuiInputBase-input': { padding: '14px 16px' },
                                }}
                            />
                            <CtaButton
                                startIcon={<SearchIcon />}
                                onClick={handleSearch}
                                sx={{ whiteSpace: 'nowrap', alignSelf: 'stretch' }}
                            >
                                Buscar
                            </CtaButton>
                        </div>
                    </div>

                    {/* Results card */}
                    <div className="bg-white rounded-20 shadow-[0_2px_16px_rgba(26,86,219,0.07)] border border-[#E2E8F0] overflow-hidden">
                        <TableContainer>
                            <Table stickyHeader aria-label="certificates table">
                                <TableHead>
                                    <TableRow>
                                        {data.cols.map((column) => (
                                            <TableCell
                                                key={column.id}
                                                align="center"
                                                sx={{
                                                    minWidth: column.minWidth,
                                                    backgroundColor: '#1E3A8A',
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                    fontSize: '1.4rem',
                                                    letterSpacing: '0.03em',
                                                }}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(() => {
                                        if (!searched) {
                                            return (
                                                <TableRow>
                                                    <TableCell colSpan={data.cols.length} align="center" sx={{ py: 6, color: '#94A3B8' }}>
                                                        Ingresa un número de certificado para comenzar la búsqueda.
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }

                                        if (filteredContent.length === 0) {
                                            return (
                                                <TableRow>
                                                    <TableCell colSpan={data.cols.length} align="center" sx={{ py: 6, color: '#94A3B8' }}>
                                                        No se encontró ningún certificado con ese número.
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }

                                        return filteredContent
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((row) => (
                                                <TableRow
                                                    hover
                                                    tabIndex={-1}
                                                    key={row.code}
                                                    sx={{ '&:hover': { backgroundColor: '#EFF6FF' } }}
                                                >
                                                    {data.cols.map((column) => {
                                                        const value = row[column.id];
                                                        const isCert = column.id === 'certificate';
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                align="center"
                                                                sx={{
                                                                    minWidth: column.minWidth,
                                                                    fontSize: '1.4rem',
                                                                    fontWeight: isCert ? 700 : 400,
                                                                    color: isCert ? '#1E3A8A' : 'inherit',
                                                                }}
                                                            >
                                                                {column.format && typeof value === 'number' ? column.format(value) : value}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            ));
                                    })()}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 100]}
                            component="div"
                            count={filteredContent.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </div>

                </div>
            </div>
        </>
    );
};

export default VerificationsCertificates;
